(ns graph-ui.web.origin
  "WHERE IS THE BUS? — answered once for the whole web client
   (PLAN-ikc-v3-021-01 phase 1, ADR-ikc-v3-090-01 §3).

   THE PAGE AND THE SOCKET HAVE ONE ORIGIN. A session is a cookie. A browser
   sends that cookie on a WebSocket upgrade only to the origin of the page,
   under SameSite=Lax. The page was on :8091 and the socket was on :4556. Those
   are two origins, so a cookie could not authenticate the socket. The bus
   transport now serves the bundle itself. Therefore the page's own origin is
   the answer, and this namespace derives it.

   TWO CALL SITES, ONE RULE. graph-ui.web.core and graph-ui.web.studio each
   built the URL from a hardcoded scheme, hostname and port 4556. Two copies of
   one rule is the CLJ/CLJS duplication this repository keeps finding by eye.
   Both call sites now read the rule from here.

   THE PATH IS DECLARED, NOT TYPED HERE. dsl/contexts/bus.edn declares
   :transports :networked-ws :routes :upgrade :path. ikc.bus.ws-server reads
   that declaration and accepts an upgrade on no other path. This namespace
   reads the same declaration through the committed mirror
   graph-ui.web.generated-bus. So the server and the client cannot disagree.

   PURE, AND DRIVEN BY AN ARGUMENT. `bus-url` takes the pieces of a location as
   a map. A node test runner has no js/location, and every case must still be
   testable — the same reason graph-ui.web.responsive/profile-for takes its
   declaration as an argument."
  (:require [clojure.string :as string]
            [graph-ui.web.generated-bus :as gen]))

;; @feature F-gi-v3-010-01-graph-ui-poc
;; @adr ADR-ikc-v3-090-01-per-person-credentials-and-partition-visibility
;; @adr ADR-ikc-v3-065-01-remote-bus-access
;; @plan PLAN-ikc-v3-021-01-a-person-signs-in

(def upgrade-path
  "The ONE path that accepts a WebSocket upgrade. Read from the mirror of
   dsl/contexts/bus.edn, never typed. An upgrade on any other path gets a 400
   that names this path."
  (get-in gen/routes [:upgrade :path]))

(defn override
  "The `?ws=` parameter of `search`, trimmed, or nil.

   About twenty .mjs harnesses under test/e2e and dev pass this parameter. Each
   one points a browser at a bus that is not the page's own origin. The
   parameter therefore stays, and it wins."
  [search]
  (when (exists? js/URLSearchParams)
    (some-> (js/URLSearchParams. (str search))
            (.get "ws")
            string/trim
            not-empty)))

(defn with-upgrade-path
  "`url` with `upgrade-path` on the end, if `url` carries no path of its own.

   WHY AN OVERRIDE GETS THE PATH. Every harness in this repository passes
   `ws://localhost:4556`, which has no path. The bus now answers 400 on a bare
   path. A verbatim override would break every one of those harnesses, and each
   failure would report a defect in the client that is not there. A path that
   the caller DID write is used unchanged, because only the caller knows why it
   is there."
  [url]
  (let [authority (let [i (string/index-of url "//")] (if i (+ i 2) 0))
        ;; A `?` or a `#` ENDS THE AUTHORITY as surely as a `/` starts the
        ;; path. Without them "ws://host:4556?x=1" would become
        ;; "ws://host:4556?x=1/bus-ws", which is not a URL at all. No harness
        ;; passes one today, so this closes a latent case rather than a live
        ;; one.
        stop      (->> [(string/index-of url "/" authority)
                        (string/index-of url "?" authority)
                        (string/index-of url "#" authority)]
                       (remove nil?)
                       (reduce min ##Inf))
        stop      (when-not (infinite? stop) stop)]
    (cond
      (nil? stop)                (str url upgrade-path)
      (= stop (dec (count url))) (str (subs url 0 stop) upgrade-path)
      (not= "/" (subs url stop (inc stop)))
      (str (subs url 0 stop) upgrade-path (subs url stop))
      :else                      url)))

(defn bus-url
  "The bus URL for a page at this location. `location` is a map of :protocol,
   :host and :search — the three parts of js/location this rule reads.

   An explicit `?ws=` parameter wins, with `with-upgrade-path` applied to it.
   Otherwise the page's own origin answers: `wss:` for an https page and `ws:`
   for any other, then the page's :host, then the declared upgrade path.

   :host AND NOT :hostname. The host carries the port. A page on
   127.0.0.1:41234 must reach the bus on 127.0.0.1:41234, because that is the
   one origin the browser will send a cookie to."
  [{:keys [protocol host search]}]
  (or (some-> (override search) with-upgrade-path)
      (str (if (= "https:" protocol) "wss://" "ws://") host upgrade-path)))

(defn page-bus-url
  "`bus-url` for the page this client runs in. The one impure function here."
  []
  (bus-url {:protocol (.-protocol js/location)
            :host     (.-host js/location)
            :search   (.-search js/location)}))

(defn without-scheme
  "`url` with a `ws://` or `wss://` prefix removed, for a label that has no room
   for it. The chrome bar removed `ws://` alone. That did nothing on an https
   page, and the label then showed the whole URL."
  [url]
  (string/replace (str url) #"^wss?://" ""))
